import React, { useEffect, useState, useCallback } from 'react';
import { MessageSquare, Search, Calendar, Loader2, ArrowLeft, User as UserIcon, Phone, Mail, Package } from 'lucide-react';
import { toast } from 'sonner';
import { adminChatAPI } from '../../utils/api';

/**
 * AdminChatTab (iter60) — read-only view of every user-to-user chat for
 * legal/compliance/dispute/security workflows. Supports search by user
 * name / email / phone / listing id / ad title / conversation id and a
 * date range. Selecting a conversation reveals the full message thread.
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const AdminChatTab = () => {
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);   // selected conversation
  const [thread, setThread] = useState(null);   // {conversation, messages}
  const [threadLoading, setThreadLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to = dateTo;
      const r = await adminChatAPI.listConversations(params);
      setConversations(r.data || []);
    } catch (_e) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const openConversation = useCallback(async (cid) => {
    setActive(cid);
    setThread(null);
    setThreadLoading(true);
    try {
      const r = await adminChatAPI.getConversationMessages(cid);
      setThread(r.data);
    } catch (_e) {
      toast.error('Failed to load conversation messages');
      setActive(null);
    } finally {
      setThreadLoading(false);
    }
  }, []);

  // Reading the full thread view
  if (active && thread) {
    const { conversation, messages } = thread;
    return (
      <div className="space-y-3" data-testid="admin-chat-thread">
        <button
          type="button"
          data-testid="admin-chat-back"
          onClick={() => { setActive(null); setThread(null); }}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1800ad] hover:text-[#ff8500] transition-colors"
        ><ArrowLeft size={14} /> Back to all conversations</button>

        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs text-gray-500">Conversation ID: <span className="font-mono text-[#0A0B10]">{conversation.conversation_id}</span></p>
            <p className="text-[10px] text-gray-400">Created {fmtDate(conversation.created_at)} · Updated {fmtDate(conversation.updated_at)}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Buyer</p>
              <p className="font-bold text-[#0A0B10]">{conversation.buyer?.name || 'Unknown'}</p>
              <p className="text-gray-500 truncate">{conversation.buyer?.email}</p>
              <p className="text-gray-500">{conversation.buyer?.phone}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Seller</p>
              <p className="font-bold text-[#0A0B10]">{conversation.seller?.name || 'Unknown'}</p>
              <p className="text-gray-500 truncate">{conversation.seller?.email}</p>
              <p className="text-gray-500">{conversation.seller?.phone}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">Listing</p>
              <p className="font-bold text-[#0A0B10] truncate">{conversation.listing?.title || '—'}</p>
              <p className="text-gray-500 font-mono text-[10px] truncate">{conversation.listing?.listing_id}</p>
              <p className="text-[#ff8500] font-bold">₹{conversation.listing?.price?.toLocaleString('en-IN')}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-3 sm:p-4 space-y-2 max-h-[600px] overflow-y-auto" data-testid="admin-chat-messages">
          {messages.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-6">No messages.</p>
          ) : messages.map((msg) => {
            const isBuyer = msg.sender_id === conversation.buyer?.user_id;
            const senderName = isBuyer ? (conversation.buyer?.name || 'Buyer') : (conversation.seller?.name || 'Seller');
            const isContact = msg.message_type === 'contact';
            const isOffer = msg.message_type === 'offer';
            return (
              <div key={msg.message_id} className="text-xs" data-testid={`admin-msg-${msg.message_id}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`font-bold ${isBuyer ? 'text-[#1800ad]' : 'text-[#ff8500]'}`}>{senderName}</span>
                  <span className="text-[10px] text-gray-400">{fmtDate(msg.created_at)}</span>
                  {isContact && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#f0edff] text-[#1800ad]">Contact</span>}
                  {isOffer && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#fff4ed] text-[#ff8500]">Offer</span>}
                  {msg.is_suspicious && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Suspicious</span>}
                </div>
                <p className="text-[#0A0B10] whitespace-pre-wrap break-words bg-gray-50 rounded-lg px-3 py-2">{msg.content}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-3" data-testid="admin-chat-list">
      <div className="flex items-center gap-2">
        <MessageSquare size={18} className="text-[#1800ad]" />
        <h2 className="text-base sm:text-lg font-bold text-[#0A0B10]">User Conversations</h2>
        <span className="text-xs text-gray-500">({conversations.length})</span>
      </div>
      <p className="text-[11px] text-gray-500 -mt-2">Read-only · for legal, dispute resolution, fraud prevention, and platform safety.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            data-testid="admin-chat-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="Search user name / email / phone / listing id / ad title…"
            className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="relative">
            <Calendar size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              data-testid="admin-chat-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white pl-7 pr-2 py-2 text-xs text-[#0A0B10] focus:border-[#1800ad] outline-none"
            />
          </div>
          <div className="relative">
            <Calendar size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              data-testid="admin-chat-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white pl-7 pr-2 py-2 text-xs text-[#0A0B10] focus:border-[#1800ad] outline-none"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500" data-testid="admin-chat-empty">No conversations match your filters.</div>
      ) : (
        <div className="space-y-2">
          {conversations.map((c) => (
            <button
              key={c.conversation_id}
              type="button"
              data-testid={`admin-chat-row-${c.conversation_id}`}
              onClick={() => openConversation(c.conversation_id)}
              className="block w-full text-left bg-white rounded-xl border border-gray-100 p-3 hover:border-[#1800ad]/40 hover:shadow-sm transition-all"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 mb-1 truncate flex items-center gap-1"><Package size={11} /> {c.listing?.title || '—'} · <span className="font-mono">{c.listing?.listing_id}</span></p>
                  <p className="text-sm font-bold text-[#0A0B10]">
                    <span className="text-[#1800ad]">{c.buyer?.name || 'Buyer'}</span>
                    <span className="text-gray-400 mx-1.5">↔</span>
                    <span className="text-[#ff8500]">{c.seller?.name || 'Seller'}</span>
                  </p>
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">
                    <Mail size={10} className="inline mr-0.5" /> {c.buyer?.email || '—'} · <Phone size={10} className="inline mr-0.5" /> {c.buyer?.phone || '—'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-gray-400">{fmtDate(c.updated_at)}</p>
                  <p className="text-xs font-bold text-[#1800ad]">{c.message_count} msg{c.message_count !== 1 ? 's' : ''}</p>
                </div>
              </div>
              {c.last_message && (
                <p className="mt-1 text-xs text-gray-600 truncate"><span className="font-semibold">Last:</span> {c.last_message.content}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminChatTab;
